/**
 * Settings tab model.
 *
 * Settings consolidates workshop configuration, templates, roles and
 * (since issue #1012) the former Equipment page's sections into a single
 * page with URL-driven tabs (?tab=…). These helpers keep the visible tab set
 * and the active tab permission-aware so a crafted ?tab= value can never
 * leave the MUI Tabs component on a tab the user is not allowed to see
 * (which would render a blank content area).
 */

export type SettingsTab =
  | 'workshop'
  | 'workshop-pricing'
  | 'workshop-hours'
  | 'numbering'
  | 'notifications'
  | 'services'
  | 'inspection-templates'
  | 'checklist-templates'
  | 'work-paths'
  | 'automations'
  | 'roles'
  | 'dashboard-defaults'
  | 'branding'
  | 'equipment'
  | 'floor-plan'
  | 'parking'
  | 'lift-status'
  | 'courtesy'
  | 'whatsapp'
  | 'svea'
  | 'sumup'
  | 'maventa'
  | 'resurs'
  | 'inbound-email'
  | 'netvisor'
  | 'sms'
  | 'os-buddy'
  | 'security'
  | 'modules'
  | 'workorders-signature';

export interface SettingsTabPermissions {
  canViewSettings?: boolean;
  canReadAutomations?: boolean;
  canManageBranding: boolean;
  canManageNumbering?: boolean;
  /** Admin-only: manage per-role dashboard default layouts (#1172). */
  canManageDashboardDefaults: boolean;
  /** Admin-only: WhatsApp Cloud API credentials and templates (#1474). */
  canManageWhatsApp: boolean;
  /**
   * Svea Payments contact request (#2806). Same permission as editing the
   * workshop identity, because the request hands that identity to Svea.
   */
  canRequestSveaContact?: boolean;
  /**
   * Maventa e-invoicing credentials (#3068). Same permission as Svea: the
   * credentials send invoices in the workshop's name.
   */
  canManageEInvoicing?: boolean;
  /**
   * SumUp card terminal credentials and pairing (#3498). Same permission as
   * Svea: the API key authorizes charging customers.
   */
  canManageCardTerminals?: boolean;
  /**
   * Resurs financing settings (#1457). Same permission as the Svea
   * credentials: the credentials act for the workshop's Resurs account.
   */
  canManageResurs?: boolean;
  /**
   * Netvisor accounting (#3022). Same permission as the other integrations
   * that act in the workshop's name: the credentials write to its books.
   */
  canManageAccounting?: boolean;
  /** SMS provider settings (#3023). Same permission: the account sends in the workshop's name. */
  canManageSms?: boolean;
  /** Teach OS Buddy workshop rules (#1608). */
  canTeachOsBuddy: boolean;
  /**
   * Session lock and logout (#2463). Same permission the idle payload was
   * already gated on when it rode the workshop save, so moving the fields to
   * their own tab cannot widen who may change them.
   */
  canEditIdleSettings?: boolean;
  /**
   * Workshop module visibility (#2646). Defaults on so a tenant that has
   * never stored the flags still sees inspection and checklist templates.
   */
  inspectionsEnabled?: boolean;
  serviceBookEnabled?: boolean;
  /** Courtesy-car register (PF-120). Manage = equipment.manage. */
  canManageEquipment?: boolean;
}

/**
 * Which part of the product a setting belongs to (#2425).
 *
 * Sixteen tabs on one scrolling row had no grouping at all, and workshop
 * settings sat between system ones. The order was not a decision: tabs were
 * appended as issues shipped, so Numbering landed second and the facility
 * tabs last regardless of what they are.
 *
 * Order now comes from the group; permissions only filter.
 */
export type SettingsGroup = 'workshop' | 'work' | 'facilities' | 'system';

export const SETTINGS_GROUP_ORDER: readonly SettingsGroup[] = [
  'workshop',
  'work',
  'facilities',
  'system',
] as const;

/**
 * Canonical group membership and display order. A tab appears here exactly
 * once; `getAllowedSettingsTabs` filters this by permission rather than
 * building its own list, so the two can never disagree.
 */
const TAB_GROUPS: Readonly<Record<SettingsGroup, readonly SettingsTab[]>> = {
  // Numbering moved out in #2622: it is the only member here behind its own
  // permission, and it owns the document-number timezone — infrastructure
  // rather than workshop identity. What is left is the workshop and what it
  // tells people.
  // Identity stays on `workshop` so existing ?tab=workshop deep links keep
  // working. Pricing and hours used to share that one save (#2428).
  workshop: [
    'workshop',
    'workshop-pricing',
    'workshop-hours',
    'notifications',
    'modules',
    'workorders-signature',
  ],
  work: ['services', 'inspection-templates', 'checklist-templates', 'work-paths', 'automations'],
  // Lift status and the parking dashboard are live views rather than settings
  // and belong in the operational navigation. Kept here for now, but as their
  // own group so moving them later touches nothing else.
  facilities: ['equipment', 'floor-plan', 'parking', 'lift-status', 'courtesy'],
  system: [
    'branding',
    'whatsapp',
    'svea',
    'sumup',
    'maventa',
    'resurs',
    'inbound-email',
    'netvisor',
    'sms',
    'os-buddy',
    'numbering',
    'security',
    'dashboard-defaults',
    'roles',
  ],
};

/** Every tab in canonical order, ignoring permissions. */
export const ALL_SETTINGS_TABS: readonly SettingsTab[] = SETTINGS_GROUP_ORDER.flatMap(
  (group) => TAB_GROUPS[group],
);

/** The group a tab belongs to, for the back link and page heading. */
export function getSettingsTabGroup(tab: SettingsTab): SettingsGroup {
  const found = SETTINGS_GROUP_ORDER.find((group) => TAB_GROUPS[group].includes(tab));
  // Unreachable while TAB_GROUPS covers the union; the fallback keeps the
  // heading sane rather than throwing at render time.
  return found ?? 'workshop';
}

/**
 * Tabs the user may see, in canonical display order. Automations retains its
 * own read permission; other tabs require settings.view and any extra grant.
 */
export function getAllowedSettingsTabs({
  canViewSettings = true,
  canReadAutomations = false,
  canManageBranding,
  canManageNumbering = false,
  canManageDashboardDefaults,
  canManageWhatsApp,
  canTeachOsBuddy,
  canEditIdleSettings = false,
  canRequestSveaContact = false,
  canManageEInvoicing = false,
  canManageCardTerminals = false,
  canManageResurs = false,
  canManageAccounting = false,
  canManageSms = false,
  inspectionsEnabled = true,
  serviceBookEnabled = true,
  canManageEquipment = false,
}: SettingsTabPermissions): SettingsTab[] {
  const isAllowed = (tab: SettingsTab): boolean => {
    if (tab === 'automations') return canReadAutomations;
    if (!canViewSettings) return false;
    switch (tab) {
      case 'numbering':
        return canManageNumbering;
      case 'os-buddy':
        return canTeachOsBuddy;
      case 'dashboard-defaults':
        return canManageDashboardDefaults;
      case 'branding':
        return canManageBranding;
      case 'whatsapp':
        return canManageWhatsApp;
      case 'svea':
        return canRequestSveaContact;
      case 'sumup':
        return canManageCardTerminals;
      case 'maventa':
        return canManageEInvoicing;
      case 'resurs':
        return canManageResurs;
      case 'netvisor':
        return canManageAccounting;
      case 'sms':
        return canManageSms;
      case 'security':
        return canEditIdleSettings;
      case 'inspection-templates':
        return inspectionsEnabled;
      case 'checklist-templates':
        return serviceBookEnabled;
      case 'courtesy':
        return canManageEquipment;
      default:
        return true;
    }
  };
  return ALL_SETTINGS_TABS.filter(isAllowed);
}

/** Allowed tabs bucketed by group, empty groups dropped. */
export function getSettingsGroups(
  allowed: SettingsTab[],
): { group: SettingsGroup; tabs: SettingsTab[] }[] {
  return SETTINGS_GROUP_ORDER.map((group) => ({
    group,
    tabs: TAB_GROUPS[group].filter((tab) => allowed.includes(tab)),
  })).filter((entry) => entry.tabs.length > 0);
}

/**
 * Clamp a requested (URL) tab to one the user is actually allowed to see.
 * Falls back to the first allowed tab, or 'workshop' when nothing is allowed.
 */
export function resolveSettingsTab(
  requested: string | null,
  allowed: SettingsTab[],
): SettingsTab {
  if (requested && (allowed as string[]).includes(requested)) {
    return requested as SettingsTab;
  }
  return allowed[0] ?? 'workshop';
}
